/**
 * DealRateCardsPanel — Phase B admin UI for scoped, per-deal rate
 * cards cloned from a master. Visible to founder + sales_manager
 * (gated by ui_flags.show_rate_card_deal).
 *
 * Capabilities:
 *   - List deals (founder sees all; sales_manager sees scope=all but
 *     write-own is enforced server-side).
 *   - Create from a master with editable lines + bundle discount.
 *   - Live guardrail-breach preview (server returns the breach list
 *     with allowed_min/max; we surface it inline so the user can
 *     adjust before retry).
 *   - Edit lines + discount post-create.
 *   - Transition state (draft → proposed → founder_approved/rejected,
 *     etc.). Founder-only transitions are flagged in the UI.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  RefreshCw, Plus, Save, AlertTriangle, CheckCircle2, X, ArrowRight,
} from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const STATE_STYLES = {
  draft:             "ek-surface-2 ek-text-soft ek-line",
  proposed:          "bg-amber-50 text-amber-800 border-amber-300",
  founder_approved:  "bg-emerald-50 text-emerald-800 border-emerald-300",
  founder_rejected:  "bg-rose-50 text-rose-700 border-rose-300",
  accepted:          "bg-[#F4EEFD] text-[#5D26C7] border-[#C8AFFB]",
  expired:           "ek-surface-2 ek-text-muted ek-line line-through",
};

const ALLOWED_TRANSITIONS = {
  draft: ["proposed"],
  proposed: ["founder_approved", "founder_rejected"],
  founder_approved: ["accepted", "expired"],
  founder_rejected: ["draft"],
  accepted: ["expired"],
  expired: [],
};

const FOUNDER_ONLY_STATES = new Set(["founder_approved", "founder_rejected"]);

const fmtGBP = (n) => `£${Number(n || 0).toLocaleString("en-GB")}`;
const fmtPct = (n) => `${Math.round(Number(n || 0) * 100)}%`;
const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

function StatePill({ state }) {
  const cls = STATE_STYLES[state] || STATE_STYLES.draft;
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 text-[10.5px] tracking-[0.12em] uppercase border ${cls}`}
      data-testid={`deal-state-pill-${state}`}
    >
      {state.replace(/_/g, " ")}
    </span>
  );
}

function BreachList({ breaches }) {
  if (!breaches || breaches.length === 0) return null;
  return (
    <div
      className="mt-3 border border-rose-300 bg-rose-50 p-3 text-[12.5px]"
      data-testid="deal-breach-list"
    >
      <div className="flex items-center gap-2 text-rose-800 font-medium">
        <AlertTriangle size={14} /> Guardrail breach — adjust before re-submitting
      </div>
      <ul className="mt-2 space-y-1 text-rose-900">
        {breaches.map((b, i) => (
          <li key={`${b.metric}-${b.role_id || "global"}-${i}`} className="leading-snug">
            <strong className="capitalize">{b.metric.replace(/_/g, " ")}</strong>
            {b.role_id ? ` (${b.role_id})` : ""}: {b.message}
          </li>
        ))}
      </ul>
    </div>
  );
}

function NumInput({ value, onChange, step = 1, min = 0, suffix, testid }) {
  return (
    <div className="inline-flex items-center gap-1">
      <input
        type="number"
        value={value ?? ""}
        min={min}
        step={step}
        onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
        className="w-24 border ek-line px-2 py-1 text-[12.5px] text-right tabular-nums focus:outline-none focus:border-[#7737F6]"
        data-testid={testid}
      />
      {suffix && <span className="text-[11px] ek-text-muted">{suffix}</span>}
    </div>
  );
}

function CreateDealModal({ masters, onClose, onCreated }) {
  const [masterId, setMasterId] = useState(masters[0]?.id || "");
  const [prospectId, setProspectId] = useState("");
  const [engagementId, setEngagementId] = useState("");
  const [bundleDiscount, setBundleDiscount] = useState(0);
  const [lines, setLines] = useState([]);
  const [breaches, setBreaches] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  // Pre-fill lines whenever the chosen master changes — start from the
  // master's roles at their master day-rates with utilisation defaulted
  // to 80% (the typical billable share). NOTE: the master's
  // `utilisation_pct` is a costing-side assumption (partner @50% etc.)
  // that doesn't always satisfy the deal-card `utilisation_min`
  // guardrail (0.60 by default), so we don't echo it directly.
  useEffect(() => {
    const master = masters.find((m) => m.id === masterId);
    if (!master) {
      setLines([]);
      return;
    }
    setLines(
      (master.roles || []).map((r) => ({
        role_id: r.role,
        day_rate_gbp: r.day_rate,
        utilisation: 0.80,
      })),
    );
    setBreaches([]);
  }, [masterId, masters]);

  const updateLine = (i, patch) => {
    setLines((prev) => {
      const next = prev.slice();
      next[i] = { ...next[i], ...patch };
      return next;
    });
  };
  const removeLine = (i) => setLines((prev) => prev.filter((_, idx) => idx !== i));
  const addLine = () => setLines((prev) => [...prev, { role_id: "", day_rate_gbp: 0, utilisation: 0.8 }]);

  const submit = async () => {
    if (!masterId) {
      toast.error("Pick a master rate card first.");
      return;
    }
    if (lines.length === 0) {
      toast.error("Add at least one line.");
      return;
    }
    setSubmitting(true);
    setBreaches([]);
    try {
      const r = await api.post("/admin/rate-cards-deal", {
        cloned_from_master_id: masterId,
        linked_prospect_id: prospectId || null,
        linked_engagement_id: engagementId || null,
        bundle_discount: Number(bundleDiscount) || 0,
        lines: lines.map((l) => ({
          role_id: String(l.role_id || ""),
          day_rate_gbp: Number(l.day_rate_gbp) || 0,
          utilisation: Number(l.utilisation) || 0,
        })),
      });
      toast.success("Deal rate card created.");
      onCreated(r.data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.error === "guardrail_breach" && Array.isArray(detail.breaches)) {
        setBreaches(detail.breaches);
        toast.error("Guardrails were breached — see inline detail.");
      } else {
        toast.error(typeof detail === "string" ? detail : "Couldn't create deal card.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 z-40 flex items-center justify-center p-4"
      data-testid="create-deal-modal"
    >
      <div className="ek-surface max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b ek-line">
          <h3 className="ek-heading text-lg">Create a deal rate card</h3>
          <button
            type="button"
            onClick={onClose}
            className="ek-text-muted hover:text-black"
            data-testid="create-deal-modal-close"
          >
            <X size={18} />
          </button>
        </div>
        <div className="p-6 space-y-5">
          <div className="grid md:grid-cols-2 gap-4">
            <label className="block text-[12px] tracking-[0.14em] uppercase ek-text-muted">
              Cloned from master
              <select
                value={masterId}
                onChange={(e) => setMasterId(e.target.value)}
                className="mt-2 w-full border ek-line px-3 py-2 text-[13px] focus:outline-none focus:border-[#7737F6] normal-case tracking-normal ek-text"
                data-testid="create-deal-master-select"
              >
                <option value="">— select —</option>
                {masters.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name} {m.is_default ? "(default)" : ""}
                  </option>
                ))}
              </select>
            </label>
            <label className="block text-[12px] tracking-[0.14em] uppercase ek-text-muted">
              Bundle discount
              <div className="mt-2">
                <NumInput
                  value={bundleDiscount}
                  onChange={setBundleDiscount}
                  step={0.05}
                  min={0}
                  suffix="(0–0.9)"
                  testid="create-deal-bundle-discount"
                />
              </div>
            </label>
            <label className="block text-[12px] tracking-[0.14em] uppercase ek-text-muted">
              Prospect id (optional)
              <input
                type="text"
                value={prospectId}
                onChange={(e) => setProspectId(e.target.value)}
                className="mt-2 w-full border ek-line px-3 py-2 text-[13px] focus:outline-none focus:border-[#7737F6] normal-case tracking-normal ek-text"
                data-testid="create-deal-prospect-id"
              />
            </label>
            <label className="block text-[12px] tracking-[0.14em] uppercase ek-text-muted">
              Engagement id (optional)
              <input
                type="text"
                value={engagementId}
                onChange={(e) => setEngagementId(e.target.value)}
                className="mt-2 w-full border ek-line px-3 py-2 text-[13px] focus:outline-none focus:border-[#7737F6] normal-case tracking-normal ek-text"
                data-testid="create-deal-engagement-id"
              />
            </label>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="ek-heading text-sm">Lines</h4>
              <button
                type="button"
                onClick={addLine}
                className="text-[12px] tracking-[0.14em] uppercase text-[#7737F6] inline-flex items-center gap-1"
                data-testid="create-deal-add-line"
              >
                <Plus size={14} /> Add line
              </button>
            </div>
            <div className="overflow-x-auto border ek-line">
              <table className="min-w-full text-[12.5px]">
                <thead className="ek-surface-2 ek-text-muted text-left text-[11px] tracking-[0.12em] uppercase">
                  <tr>
                    <th className="px-3 py-2">Role</th>
                    <th className="px-3 py-2">Day rate (GBP)</th>
                    <th className="px-3 py-2">Utilisation</th>
                    <th className="px-3 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {lines.map((l, i) => (
                    <tr key={`line-${i}-${l.role_id}`} className="border-t ek-line">
                      <td className="px-3 py-2">
                        <input
                          type="text"
                          value={l.role_id}
                          onChange={(e) => updateLine(i, { role_id: e.target.value })}
                          className="w-full border ek-line px-2 py-1 text-[12.5px] focus:outline-none focus:border-[#7737F6]"
                          data-testid={`create-deal-line-role-${i}`}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <NumInput
                          value={l.day_rate_gbp}
                          onChange={(v) => updateLine(i, { day_rate_gbp: v })}
                          testid={`create-deal-line-rate-${i}`}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <NumInput
                          value={l.utilisation}
                          onChange={(v) => updateLine(i, { utilisation: v })}
                          step={0.05}
                          suffix="(0–1)"
                          testid={`create-deal-line-util-${i}`}
                        />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button
                          type="button"
                          onClick={() => removeLine(i)}
                          className="text-rose-600 text-[11.5px] tracking-[0.12em] uppercase"
                          data-testid={`create-deal-line-remove-${i}`}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                  {lines.length === 0 && (
                    <tr>
                      <td colSpan={4} className="px-3 py-6 text-center ek-text-muted">
                        No lines yet. Add at least one before submitting.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <BreachList breaches={breaches} />
        </div>

        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t ek-line ek-surface-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-[12px] tracking-[0.14em] uppercase ek-text-soft hover:text-black"
            data-testid="create-deal-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={submitting}
            className="ek-btn bg-[#7737F6] text-white text-[12px] tracking-[0.14em] uppercase px-5 py-2 disabled:opacity-60"
            data-testid="create-deal-submit"
          >
            {submitting ? "Submitting…" : "Create deal card"}
          </button>
        </div>
      </div>
    </div>
  );
}

function TransitionMenu({ deal, onTransitioned }) {
  const targets = ALLOWED_TRANSITIONS[deal.state] || [];
  const [working, setWorking] = useState(false);
  if (targets.length === 0) return <span className="text-[11.5px] ek-text-muted">No further transitions</span>;

  const move = async (to_state) => {
    setWorking(true);
    try {
      const r = await api.post(`/admin/rate-cards-deal/${deal.id}/transition`, {
        to_state,
        reason: "",
      });
      toast.success(`Moved to ${to_state}`);
      onTransitioned(r.data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = detail?.message || (typeof detail === "string" ? detail : "Transition rejected");
      toast.error(msg);
    } finally {
      setWorking(false);
    }
  };

  return (
    <div className="flex flex-wrap gap-2" data-testid={`deal-transitions-${deal.id}`}>
      {targets.map((t) => (
        <button
          key={t}
          type="button"
          onClick={() => move(t)}
          disabled={working}
          className={`inline-flex items-center gap-1 text-[11px] tracking-[0.12em] uppercase border px-2 py-1 disabled:opacity-60 ${
            FOUNDER_ONLY_STATES.has(t)
              ? "border-[#7737F6] text-[#7737F6] hover:bg-[#F4EEFD]"
              : "ek-line ek-text-soft hover:border-black hover:text-black"
          }`}
          data-testid={`deal-transition-${deal.id}-${t}`}
          title={FOUNDER_ONLY_STATES.has(t) ? "Founder only" : ""}
        >
          <ArrowRight size={11} /> {t.replace(/_/g, " ")}
        </button>
      ))}
    </div>
  );
}

function DealDeltas({ deal, master }) {
  if (!master) return null;
  const masterByRole = {};
  for (const r of master.roles || []) {
    masterByRole[r.role] = r;
  }
  const rows = (deal.lines || []).map((l) => {
    const m = masterByRole[l.role_id];
    if (!m) {
      return { role: l.role_id, rateDeltaPct: null, utilDelta: null, unknown: true };
    }
    const masterRate = Number(m.day_rate || 0);
    const masterUtil = (Number(m.utilisation_pct || 0)) / 100;
    const rateDeltaPct = masterRate > 0 ? ((Number(l.day_rate_gbp) - masterRate) / masterRate) * 100 : 0;
    const utilDelta = (Number(l.utilisation) - masterUtil) * 100; // percentage points
    return { role: l.role_id, rateDeltaPct, utilDelta, unknown: false };
  });
  if (rows.length === 0) return null;

  const renderRateChip = (v) => {
    if (v === null) return <span className="text-[10.5px] ek-text-muted">—</span>;
    const rounded = Math.round(v * 10) / 10;
    if (Math.abs(rounded) < 0.05) {
      return <span className="text-[10.5px] ek-text-muted">at master</span>;
    }
    const cls = rounded > 0
      ? "text-emerald-700 bg-emerald-50 border-emerald-200"
      : "text-rose-700 bg-rose-50 border-rose-200";
    const sign = rounded > 0 ? "+" : "";
    return (
      <span className={`inline-flex items-center text-[10.5px] tabular-nums px-1.5 py-0.5 border ${cls}`}>
        {sign}{rounded.toFixed(1)}%
      </span>
    );
  };

  const renderUtilChip = (v) => {
    if (v === null) return <span className="text-[10.5px] ek-text-muted">—</span>;
    const rounded = Math.round(v);
    if (rounded === 0) {
      return <span className="text-[10.5px] ek-text-muted">at master</span>;
    }
    const cls = rounded > 0
      ? "text-emerald-700 bg-emerald-50 border-emerald-200"
      : "text-rose-700 bg-rose-50 border-rose-200";
    const sign = rounded > 0 ? "+" : "";
    return (
      <span className={`inline-flex items-center text-[10.5px] tabular-nums px-1.5 py-0.5 border ${cls}`}>
        {sign}{rounded}pp
      </span>
    );
  };

  return (
    <div
      className="mb-4 border ek-line ek-surface p-3"
      data-testid={`deal-deltas-${deal.id}`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] tracking-[0.14em] uppercase ek-text-muted">
          Δ vs master <span className="ek-text-muted normal-case tracking-normal">— {master.name}</span>
        </div>
        <div className="text-[11px] tracking-[0.14em] uppercase ek-text-muted">
          Bundle discount{" "}
          <span className="ek-text-soft normal-case tracking-normal">
            {fmtPct(deal.bundle_discount)}
          </span>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-[12px]">
          <thead className="ek-text-muted text-left text-[10.5px] tracking-[0.12em] uppercase">
            <tr>
              <th className="pr-4 pb-1.5">Role</th>
              <th className="pr-4 pb-1.5">Δ Day rate</th>
              <th className="pr-4 pb-1.5">Δ Utilisation</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={`delta-${i}-${r.role}`} className="ek-text">
                <td className="pr-4 py-0.5">
                  {r.role || <span className="ek-text-muted">(unnamed)</span>}
                  {r.unknown && (
                    <span className="ml-2 text-[10px] text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5">
                      not in master
                    </span>
                  )}
                </td>
                <td className="pr-4 py-0.5">{renderRateChip(r.rateDeltaPct)}</td>
                <td className="pr-4 py-0.5">{renderUtilChip(r.utilDelta)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DealRow({ deal, master, expanded, onToggle, onUpdated }) {
  const [editLines, setEditLines] = useState(deal.lines || []);
  const [editDiscount, setEditDiscount] = useState(deal.bundle_discount || 0);
  const [breaches, setBreaches] = useState([]);
  const [saving, setSaving] = useState(false);
  const locked = ["founder_approved", "accepted", "expired"].includes(deal.state);

  useEffect(() => {
    setEditLines(deal.lines || []);
    setEditDiscount(deal.bundle_discount || 0);
    setBreaches([]);
  }, [deal.id, deal.lines, deal.bundle_discount]);

  const save = async () => {
    setSaving(true);
    setBreaches([]);
    try {
      const r = await api.put(`/admin/rate-cards-deal/${deal.id}`, {
        bundle_discount: Number(editDiscount) || 0,
        lines: editLines.map((l) => ({
          role_id: String(l.role_id || ""),
          day_rate_gbp: Number(l.day_rate_gbp) || 0,
          utilisation: Number(l.utilisation) || 0,
        })),
      });
      toast.success("Deal card updated.");
      onUpdated(r.data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.error === "guardrail_breach" && Array.isArray(detail.breaches)) {
        setBreaches(detail.breaches);
        toast.error("Guardrail breach on update.");
      } else {
        toast.error(typeof detail === "string" ? detail : "Update failed.");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <tr className="border-t ek-line hover:bg-zinc-50" data-testid={`deal-row-${deal.id}`}>
        <td className="px-3 py-2 font-mono text-[11.5px] ek-text-muted">{deal.id}</td>
        <td className="px-3 py-2"><StatePill state={deal.state} /></td>
        <td className="px-3 py-2">{deal.linked_prospect_id || deal.linked_engagement_id || "—"}</td>
        <td className="px-3 py-2 tabular-nums">{(deal.lines || []).length}</td>
        <td className="px-3 py-2 tabular-nums">{fmtPct(deal.bundle_discount)}</td>
        <td className="px-3 py-2 tabular-nums">{fmtPct(deal.computed_effective_margin)}</td>
        <td className="px-3 py-2 text-[11.5px] ek-text-muted">{fmtDate(deal.last_modified_at)}</td>
        <td className="px-3 py-2 text-right">
          <button
            type="button"
            onClick={onToggle}
            className="text-[11.5px] tracking-[0.12em] uppercase text-[#7737F6]"
            data-testid={`deal-toggle-${deal.id}`}
          >
            {expanded ? "Collapse" : "Open"}
          </button>
        </td>
      </tr>
      {expanded && (
        <tr className="ek-surface-2">
          <td colSpan={8} className="px-6 py-5 border-t ek-line">
            <DealDeltas deal={deal} master={master} />
            <div className="grid lg:grid-cols-3 gap-5">
              <div className="lg:col-span-2">
                <h5 className="ek-heading text-sm mb-3">Lines</h5>
                <div className="overflow-x-auto border ek-line ek-surface">
                  <table className="min-w-full text-[12.5px]">
                    <thead className="ek-surface-2 ek-text-muted text-left text-[11px] tracking-[0.12em] uppercase">
                      <tr>
                        <th className="px-3 py-2">Role</th>
                        <th className="px-3 py-2">Day rate (GBP)</th>
                        <th className="px-3 py-2">Utilisation</th>
                      </tr>
                    </thead>
                    <tbody>
                      {editLines.map((l, i) => (
                        <tr key={`edit-line-${i}-${l.role_id}`} className="border-t ek-line">
                          <td className="px-3 py-2">
                            <input
                              type="text"
                              value={l.role_id}
                              disabled={locked}
                              onChange={(e) => {
                                const next = editLines.slice();
                                next[i] = { ...next[i], role_id: e.target.value };
                                setEditLines(next);
                              }}
                              className="w-full border ek-line px-2 py-1 text-[12.5px] focus:outline-none focus:border-[#7737F6] disabled:bg-zinc-100"
                              data-testid={`deal-edit-role-${deal.id}-${i}`}
                            />
                          </td>
                          <td className="px-3 py-2">
                            <NumInput
                              value={l.day_rate_gbp}
                              onChange={(v) => {
                                const next = editLines.slice();
                                next[i] = { ...next[i], day_rate_gbp: v };
                                setEditLines(next);
                              }}
                              testid={`deal-edit-rate-${deal.id}-${i}`}
                            />
                          </td>
                          <td className="px-3 py-2">
                            <NumInput
                              value={l.utilisation}
                              onChange={(v) => {
                                const next = editLines.slice();
                                next[i] = { ...next[i], utilisation: v };
                                setEditLines(next);
                              }}
                              step={0.05}
                              suffix="(0–1)"
                              testid={`deal-edit-util-${deal.id}-${i}`}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="mt-4 flex items-center gap-4">
                  <label className="text-[12px] tracking-[0.14em] uppercase ek-text-muted inline-flex items-center gap-2">
                    Bundle discount
                    <NumInput
                      value={editDiscount}
                      onChange={setEditDiscount}
                      step={0.05}
                      suffix="(0–0.9)"
                      testid={`deal-edit-discount-${deal.id}`}
                    />
                  </label>
                  <button
                    type="button"
                    onClick={save}
                    disabled={saving || locked}
                    className="ek-btn bg-[#7737F6] text-white text-[12px] tracking-[0.14em] uppercase px-4 py-2 disabled:opacity-60 inline-flex items-center gap-2"
                    data-testid={`deal-edit-save-${deal.id}`}
                  >
                    <Save size={13} /> {saving ? "Saving…" : "Save"}
                  </button>
                  {locked && (
                    <span className="text-[11.5px] ek-text-muted">
                      Locked in {deal.state} — founder approval needed to edit
                    </span>
                  )}
                </div>
                <BreachList breaches={breaches} />
              </div>
              <div>
                <h5 className="ek-heading text-sm mb-3">State machine</h5>
                <TransitionMenu deal={deal} onTransitioned={onUpdated} />
                {deal.audit && deal.audit.length > 0 && (
                  <div className="mt-5">
                    <div className="text-[11px] tracking-[0.14em] uppercase ek-text-muted mb-2">History</div>
                    <ul className="space-y-1 text-[12px]">
                      {deal.audit.map((a, i) => (
                        <li key={`audit-${i}-${a.at}`} className="ek-text-soft">
                          <span className="font-mono text-[11px] ek-text-muted">{fmtDate(a.at)}</span>
                          {" — "}
                          <span className="capitalize">{(a.before?.state || "").replace(/_/g, " ")}</span>
                          {" → "}
                          <span className="capitalize font-medium">{(a.after?.state || "").replace(/_/g, " ")}</span>
                          {a.reason ? <span className="ek-text-muted"> · {a.reason}</span> : null}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export default function DealRateCardsPanel() {
  const [deals, setDeals] = useState([]);
  const [masters, setMasters] = useState([]);
  const [stateFilter, setStateFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, m] = await Promise.all([
        api.get(`/admin/rate-cards-deal${stateFilter ? `?state=${stateFilter}` : ""}`),
        api.get("/admin/rate-cards"),
      ]);
      setDeals(d.data?.items || []);
      setMasters(m.data?.items || []);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 403) {
        toast.error("You don't have access to deal rate cards.");
      } else {
        toast.error("Couldn't load deal rate cards.");
      }
    } finally {
      setLoading(false);
    }
  }, [stateFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const upsert = (updated) => {
    setDeals((prev) => {
      const idx = prev.findIndex((d) => d.id === updated.id);
      if (idx === -1) return [updated, ...prev];
      const next = prev.slice();
      next[idx] = updated;
      return next;
    });
  };

  const counts = useMemo(() => {
    return deals.reduce((acc, d) => {
      acc[d.state] = (acc[d.state] || 0) + 1;
      return acc;
    }, {});
  }, [deals]);

  const mastersById = useMemo(() => {
    const m = {};
    for (const card of masters) {
      m[card.id] = card;
    }
    return m;
  }, [masters]);

  return (
    <div data-testid="deal-rate-cards-panel">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h2 className="ek-heading text-xl">Deal rate cards</h2>
          <p className="text-[13px] ek-text-muted mt-1 max-w-2xl">
            Scoped, guardrail-bounded clones of a master rate card. Sales managers
            create deal-specific pricing; founder approval is required to lock a
            deal as <em>founder_approved</em>.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={stateFilter}
            onChange={(e) => setStateFilter(e.target.value)}
            className="border ek-line px-3 py-2 text-[12.5px]"
            data-testid="deal-state-filter"
          >
            <option value="">All states</option>
            {Object.keys(STATE_STYLES).map((s) => (
              <option key={s} value={s}>
                {s.replace(/_/g, " ")} {counts[s] ? `(${counts[s]})` : ""}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={load}
            className="ek-btn ek-btn-outline text-[12px] inline-flex items-center gap-2"
            data-testid="deal-refresh"
          >
            <RefreshCw size={13} /> Refresh
          </button>
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            disabled={masters.length === 0}
            className="ek-btn bg-[#7737F6] text-white text-[12px] tracking-[0.14em] uppercase px-4 py-2 inline-flex items-center gap-2 disabled:opacity-60"
            data-testid="deal-create-btn"
          >
            <Plus size={13} /> New deal card
          </button>
        </div>
      </div>

      {masters.length === 0 && !loading && (
        <div className="border border-amber-300 bg-amber-50 p-4 mb-5 text-[13px] text-amber-900 inline-flex items-center gap-2">
          <AlertTriangle size={14} />
          No master rate cards yet — create one in the <strong>Rate cards</strong> tab first.
        </div>
      )}

      <div className="overflow-x-auto border ek-line">
        <table className="min-w-full text-[13px]">
          <thead className="ek-surface-2 ek-text-muted text-left text-[11px] tracking-[0.12em] uppercase">
            <tr>
              <th className="px-3 py-2">Deal id</th>
              <th className="px-3 py-2">State</th>
              <th className="px-3 py-2">Prospect / engagement</th>
              <th className="px-3 py-2">Lines</th>
              <th className="px-3 py-2">Discount</th>
              <th className="px-3 py-2">Eff. margin</th>
              <th className="px-3 py-2">Last modified</th>
              <th className="px-3 py-2 text-right" />
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={8} className="px-3 py-8 text-center ek-text-muted">
                  Loading…
                </td>
              </tr>
            )}
            {!loading && deals.length === 0 && (
              <tr>
                <td colSpan={8} className="px-3 py-10 text-center">
                  <div className="ek-text-soft font-medium" data-testid="deal-empty-headline">
                    No deal rate cards yet.
                  </div>
                  <div className="ek-text-muted text-[12.5px] mt-1">
                    Clone a master rate card to scope pricing for a specific prospect or
                    engagement — bounded by the founder-set guardrails.
                  </div>
                </td>
              </tr>
            )}
            {!loading &&
              deals.map((d) => (
                <DealRow
                  key={d.id}
                  deal={d}
                  master={mastersById[d.cloned_from_master_id]}
                  expanded={expanded === d.id}
                  onToggle={() => setExpanded((cur) => (cur === d.id ? null : d.id))}
                  onUpdated={upsert}
                />
              ))}
          </tbody>
        </table>
      </div>

      {showCreate && (
        <CreateDealModal
          masters={masters}
          onClose={() => setShowCreate(false)}
          onCreated={(deal) => {
            upsert(deal);
            setShowCreate(false);
            setExpanded(deal.id);
          }}
        />
      )}
    </div>
  );
}
